import { useCallback, useMemo, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import type { SelectAction, SelectOption } from "@outpave/ds";
import {
  allocationOption,
  fetchAllocationPage,
} from "utils/allocations/allocationOptions";
import { useSelectSearch, type SelectSearch } from "./useSelectSearch";
import { useBrowseAllAllocations } from "./useBrowseAllAllocations";

/** The saved allocation a field starts on, so it renders before any search. */
export interface AllocationSeed {
  id: string;
  /** Falls back to the id when the saved record has no code yet. */
  allocationCode?: string | null;
  allocationTypeName?: string | null;
}

/** The allocation type a field is scoped to, when it is scoped to one. */
export interface AllocationTypeFilter {
  key: string;
  name: string;
}

export interface AllocationSearch extends SelectSearch<SelectOption> {
  /**
   * Domain read-back for `onChange`. Keeps `caption` a display concern rather
   * than the channel call sites pull the allocation type out of.
   */
  allocationById: (
    id: string,
  ) =>
    | { id: string; allocationCode: string; allocationTypeName: string }
    | undefined;
  /** For the Select: recent picks come first, so an empty list asks for a search rather than saying none exist. */
  noOptionsText: string;
  /** For the Select: switches between recent picks and every allocation. */
  bottomAction?: SelectAction;
}

/**
 * Allocation options for a DS `Select`: the user's recent picks until they type,
 * then a backend search. Allocations can run to thousands, so the list is never loaded whole.
 */
export function useAllocationSelectOptions(
  seed?: AllocationSeed | null,
  /**
   * Narrows the search to one allocation type. A custom field of type
   * ALLOCATION carries its type key in `options[0]`, and an unfiltered picker
   * lets the user save an allocation the field was never meant to collect.
   */
  allocationType?: AllocationTypeFilter | null,
): AllocationSearch {
  const client = useApolloClient();
  const { t } = useTranslation();
  const typeKey = allocationType?.key ?? null;
  const typeName = allocationType?.name ?? null;
  const { browseAll, browseAction } = useBrowseAllAllocations();

  const seedId = seed?.id ?? null;
  const seedCode = seed?.allocationCode ?? null;
  const seedType = seed?.allocationTypeName ?? null;
  // Memoized here so call sites can build the seed inline.
  const seedOption = useMemo<SelectOption | null>(
    () =>
      seedId
        ? {
            id: seedId,
            label: seedCode || seedId,
            caption: seedType ?? undefined,
          }
        : null,
    [seedId, seedCode, seedType],
  );

  // The caption is display text (a parent path or a type), so the type rides beside it for read-back.
  const typeNameById = useRef(new Map<string, string>());

  const fetchPage = useCallback(
    async (query: string, after: string | null) => {
      const page = await fetchAllocationPage(client, {
        query,
        after,
        allocationType:
          typeKey && typeName ? { key: typeKey, name: typeName } : null,
        browseAll,
      });
      page.nodes.forEach((node) =>
        typeNameById.current.set(node.id, node.allocationType?.name ?? ""),
      );
      return {
        options: page.nodes.map((node) => allocationOption(node, page.recent)),
        hasNextPage: page.hasNextPage,
        endCursor: page.endCursor,
      };
    },
    [client, typeKey, typeName, browseAll],
  );

  const search = useSelectSearch<SelectOption>({
    fetchPage,
    seed: seedOption,
    refetchKey: `${typeKey ?? ""}|${browseAll}`,
  });
  const { optionById } = search;

  const allocationById = useCallback(
    (id: string) => {
      const option = optionById(id);
      return option
        ? {
            id,
            allocationCode: option.label,
            allocationTypeName:
              typeNameById.current.get(id) ?? seedType ?? option.caption ?? "",
          }
        : undefined;
    },
    [optionById, seedType],
  );

  const noOptionsText =
    search.query || browseAll
      ? t("No allocations found")
      : t("NoRecentAllocations");

  return {
    ...search,
    allocationById,
    noOptionsText,
    bottomAction: browseAction(search.query),
  };
}
